import { action, observable, store } from '~/common/mobx.decorator'
import { persist } from 'mobx-persist'
import { DEFAULT_TABLE_LOAD_BATCH, TABLE_LOAD_BATCH_OPTIONS } from '~/common/constants/table.constant'

/**
 * Per-user view preferences for the recruiter tables: which columns are hidden,
 * and how the rows that are not on screen arrive.
 *
 * WHY THIS IS SEPARATE FROM `genericTableStore`
 * ---------------------------------------------
 * `genericTableStore.appliedFilters` is a BE-backed contract — the same
 * `tableName` keys a `saved_filter_view` row on the server, and a saved view is
 * a shareable artefact. These are the opposite: per-user chrome, persisted to
 * localStorage rather than to anybody else's account. Keeping them in their own
 * store makes that boundary explicit rather than a rule someone has to remember.
 *
 * Column visibility is the one exception, and it is deliberate: a saved view now
 * carries the column layout it was saved with (`savedFilterViewsStore`), so
 * applying a view writes back through `setHiddenColumns`. Nothing else here
 * travels — a colleague's view never changes how your rows are paged.
 *
 * Keyed by the same `tableName` values (see `table-name.constant.js`) so a
 * preference follows the tab it was set on.
 */
@store({ persist: true, persistKey: 'TablePreferencesStore' })
class TablePreferencesStore {
	/** { [tableName]: string[] } — column `key`s the user has switched off. */
	@persist('object') @observable hiddenColumns = {}

	/**
	 * { [tableName]: string[] } — the user's column order, as the `key`s of the
	 * HIDEABLE columns only, in the order they should appear.
	 *
	 * Locked columns (the leading control, the identity column, the row actions)
	 * are deliberately absent. They hold the positions they were declared in and
	 * cannot be moved: `frozenColumnCount` pins the leading columns so the row
	 * stays identifiable while you scroll 2400px sideways, and letting the
	 * identity column be dragged to position nine would freeze an eye icon and a
	 * date instead. So the order describes the slots the user is allowed to
	 * rearrange, and `applyColumnOrder` refills exactly those slots.
	 *
	 * An absent entry means "no opinion" — the columns arrive in declaration
	 * order, which is what every table did before this existed.
	 */
	@persist('object') @observable columnOrder = {}

	/**
	 * { [tableName]: string[] } — the columns the user has pinned, in pin order.
	 *
	 * AG Grid's model: pinning is PER COLUMN and the column moves into the pinned
	 * region on the left, so the seventh column can be pinned without dragging the
	 * six before it along. A spreadsheet freeze — Airtable's divider, Excel's
	 * "freeze up to column X" — is a single boundary and cannot express that; this
	 * held such a boundary until it was decided that per-column was the better fit
	 * for tables where the interesting columns are scattered.
	 *
	 * Pin order is the array order, so pinning a second column puts it beside the
	 * first rather than somewhere derived from the table's own layout.
	 *
	 * Values written by the previous boundary implementation were a single string;
	 * `getPinnedColumnKeys` still reads those, so nobody's stored preference or
	 * saved view breaks on upgrade.
	 */
	@persist('object') @observable frozenColumns = {}

	/** Rows per infinite-scroll batch. */
	@persist @observable loadBatchSize = DEFAULT_TABLE_LOAD_BATCH

	@action
	setLoadBatchSize = (size) => {
		this.loadBatchSize = TABLE_LOAD_BATCH_OPTIONS.includes(size) ? size : DEFAULT_TABLE_LOAD_BATCH
	}

	/**
	 * Has this user made a choice about this table's columns at all?
	 *
	 * The difference matters: no entry means "show whatever the table ships with",
	 * which is NOT the same as an empty entry, which means "I switched everything
	 * on". Only the first falls back to the columns marked `defaultHidden`.
	 */
	hasHiddenColumnsPreference(tableName) {
		return Boolean(tableName) && Object.prototype.hasOwnProperty.call(this.hiddenColumns, tableName)
	}

	/**
	 * The pinned column keys for a table, always an array.
	 *
	 * Tolerates the single string the boundary implementation used to store, and
	 * the empty string it used for "pin nothing".
	 */
	getPinnedColumnKeys(tableName) {
		if (!tableName) return []
		const value = this.frozenColumns[tableName]
		if (Array.isArray(value)) return value.filter((key) => typeof key === 'string' && key)
		if (typeof value === 'string' && value) return [value]
		return []
	}

	isColumnPinned(tableName, columnKey) {
		return this.getPinnedColumnKeys(tableName).includes(columnKey)
	}

	@action
	setPinnedColumnKeys = (tableName, columnKeys) => {
		if (!tableName) return
		const next = { ...this.frozenColumns }
		if (!Array.isArray(columnKeys) || !columnKeys.length) delete next[tableName]
		else next[tableName] = [...columnKeys]
		this.frozenColumns = next
	}

	/**
	 * Pin or unpin one column. Newly pinned columns go to the END of the pinned
	 * run, so an existing pin never shifts out from under the user.
	 */
	@action
	togglePinnedColumn = (tableName, columnKey) => {
		if (!tableName || !columnKey) return
		const current = this.getPinnedColumnKeys(tableName)
		this.setPinnedColumnKeys(tableName, current.includes(columnKey) ? current.filter((key) => key !== columnKey) : [...current, columnKey])
	}

	getHiddenColumns(tableName) {
		if (!tableName) return []
		return this.hiddenColumns[tableName] || []
	}

	isColumnHidden(tableName, columnKey) {
		return this.getHiddenColumns(tableName).includes(columnKey)
	}

	/**
	 * Flip one column's visibility. The convenience form of `setColumnHidden`, and
	 * deliberately nothing more than that: two visibility APIs with two different
	 * side effects — one that moves the column between groups and one that does
	 * not — is a trap, and the panel would look wrong whichever one got picked by
	 * mistake.
	 */
	@action
	toggleColumn = (tableName, columnKey) => {
		if (!tableName || !columnKey) return
		this.setColumnHidden(tableName, columnKey, !this.isColumnHidden(tableName, columnKey))
	}

	/**
	 * Replace a table's whole hidden set at once — what applying a saved view
	 * does. `null`/`undefined` means "this view has no opinion about columns"
	 * (every view saved before views carried a layout) and leaves the user's
	 * current choice alone; an empty array means "show every column" and is
	 * applied like any other layout.
	 */
	@action
	setHiddenColumns = (tableName, columnKeys) => {
		if (!tableName || !Array.isArray(columnKeys)) return
		/*
		 * An empty list is STORED, not cleared.
		 *
		 * "Hide nothing" and "I have never touched this table" are different
		 * answers — `hasHiddenColumnsPreference` above says so — and only the
		 * second one may fall back to the columns the table ships hidden. Deleting
		 * the entry here collapsed the two, so `defaultHidden` was re-seeded on the
		 * next render and every "show everything" was quietly undone:
		 *
		 *   - Settings › "Show all" put the columns back a moment later;
		 *   - applying a saved view that hides nothing restored its column ORDER
		 *     and then hid the columns again — measured on Available Jobs, a view
		 *     saved with 33 columns came back with 11.
		 *
		 * Clearing is still what Reset means, and `resetColumns` still does it.
		 */
		this.hiddenColumns = { ...this.hiddenColumns, [tableName]: [...columnKeys] }
	}

	@action
	clearHiddenColumns = (tableName) => {
		if (!tableName) return
		const next = { ...this.hiddenColumns }
		delete next[tableName]
		this.hiddenColumns = next
	}

	getColumnOrder(tableName) {
		if (!tableName) return []
		return this.columnOrder[tableName] || []
	}

	/**
	 * Replace a table's column order. `null`/`undefined` means "this view has no
	 * opinion about order" and leaves the user's own arrangement alone — the same
	 * contract `setHiddenColumns` has, and the shape every view saved before
	 * ordering existed comes back as.
	 */
	@action
	setColumnOrder = (tableName, columnKeys) => {
		if (!tableName || !Array.isArray(columnKeys)) return
		if (!columnKeys.length) {
			const next = { ...this.columnOrder }
			delete next[tableName]
			this.columnOrder = next
			return
		}
		this.columnOrder = { ...this.columnOrder, [tableName]: [...columnKeys] }
	}

	/**
	 * Move one column a step earlier or later among `orderedKeys` — the hideable
	 * keys as they are currently displayed, which the caller already has and the
	 * store does not (it never sees a table's column list).
	 *
	 * This is the keyboard half of dragging: the drag handle takes focus and
	 * Arrow Up / Arrow Down land here, so arranging columns does not require a
	 * pointer.
	 */
	@action
	moveColumn = (tableName, columnKey, delta, orderedKeys) => {
		if (!tableName || !columnKey || !Array.isArray(orderedKeys)) return
		const from = orderedKeys.indexOf(columnKey)
		const to = from + delta
		if (from < 0 || to < 0 || to >= orderedKeys.length) return
		const next = [...orderedKeys]
		next.splice(to, 0, next.splice(from, 1)[0])
		this.setColumnOrder(tableName, next)
	}

	/** Drop `columnKey` onto `targetKey`'s slot — the pointer half of the same. */
	@action
	moveColumnTo = (tableName, columnKey, targetKey, orderedKeys) => {
		if (!tableName || !columnKey || !targetKey || !Array.isArray(orderedKeys)) return
		const from = orderedKeys.indexOf(columnKey)
		const to = orderedKeys.indexOf(targetKey)
		if (from < 0 || to < 0 || from === to) return
		const next = [...orderedKeys]
		next.splice(to, 0, next.splice(from, 1)[0])
		this.setColumnOrder(tableName, next)
	}

	/**
	 * Show or hide one column, and move it to the end of the group it just
	 * joined.
	 *
	 * The move is the point. The panel lists shown columns and hidden ones as two
	 * separate groups, so a column that changes group has to go somewhere visible
	 * in the new one — "back where it used to be" is a position the user cannot
	 * see and therefore cannot predict. Hiding sends it to the end of the hidden
	 * group; showing brings it back at the end of the shown group.
	 */
	@action
	setColumnHidden = (tableName, columnKey, hidden, orderedKeys) => {
		if (!tableName || !columnKey) return
		const current = this.getHiddenColumns(tableName)
		const nextHidden = hidden ? [...current.filter((key) => key !== columnKey), columnKey] : current.filter((key) => key !== columnKey)

		const keys = Array.isArray(orderedKeys) && orderedKeys.length ? [...orderedKeys] : [...this.getColumnOrder(tableName)]
		const without = keys.filter((key) => key !== columnKey)
		let nextOrder
		if (hidden) {
			// End of the hidden group is the end of the list.
			nextOrder = [...without, columnKey]
		} else {
			const firstHidden = without.findIndex((key) => nextHidden.includes(key))
			const at = firstHidden === -1 ? without.length : firstHidden
			nextOrder = [...without.slice(0, at), columnKey, ...without.slice(at)]
		}

		this.setHiddenColumns(tableName, nextHidden)
		this.setColumnOrder(tableName, nextOrder)
	}

	/** "Hide all" / "Show all" on a group header. Order is left as it is. */
	@action
	setAllColumnsHidden = (tableName, columnKeys, hidden) => {
		if (!tableName || !Array.isArray(columnKeys)) return
		// "Show all" is a choice the user made, so it is recorded as one. Clearing
		// the entry instead would read as "never configured" and hand the table
		// straight back to its `defaultHidden` columns.
		this.setHiddenColumns(tableName, hidden ? [...columnKeys] : [])
	}

	/** Back to the table as it ships: every column showing, declaration order. */
	/**
	 * Back to the table as it ships — which includes its `defaultHidden` columns
	 * being hidden again, hence deleting the entry rather than emptying it, and
	 * the freeze line going back to the table's own judgement.
	 */
	@action
	resetColumns = (tableName) => {
		if (!tableName) return
		this.clearHiddenColumns(tableName)
		const nextOrder = { ...this.columnOrder }
		delete nextOrder[tableName]
		this.columnOrder = nextOrder
		this.setPinnedColumnKeys(tableName, [])
	}
}

export const tablePreferencesStore = new TablePreferencesStore()
